import { useEffect, useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import {
  useInstagramConversations,
  instagramConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import { CRMConversationFilter, CRMConversationMode } from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { InstagramConversationTile } from "../InstagramConversationTile";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";

export interface InstagramConversationsProps {
  searchQuery: string;
  selectedFilter: CRMConversationFilter;
  selectedMode: CRMConversationMode;
}

export const InstagramConversations: React.FC<InstagramConversationsProps> = ({
  selectedMode,
  searchQuery,
  selectedFilter,
}) => {
  const { t } = useTranslations();
  const {
    selectedConversation,
    selectConversation,
    incomingInstagramMessages$,
  } = useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  // Compute filter for Instagram based on selected mode
  const computedFilter = useMemo(() => {
    if (selectedFilter === CRMConversationFilter.active) {
      switch (selectedMode) {
        case CRMConversationMode.managedByMe:
          return me?.id || "";
        case CRMConversationMode.waitingResponse:
        case CRMConversationMode.onAIAutoReply:
          return selectedMode;
        default:
          return CRMConversationFilter.active;
      }
    }

    return CRMConversationFilter.expired;
  }, [selectedFilter, selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    reset: resetConversations,
    retry,
    loadMore,
  } = useInstagramConversations({
    entityId,
    searchTerm: searchQuery,
    filter: computedFilter,
    pageSize: 10,
    skip: selectedFilter === CRMConversationFilter.blocked,
  });

  useSelectedConversation(conversations);

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingInstagramMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        // Prepend new conversation to the top
        instagramConversations$.next({
          type: "prepend",
          conversation: message as InstagramConversationModel,
        });
        return;
      }

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as InstagramConversationModel).inserted_at >=
              message.inserted_at) ||
          message.msg_status === "new"
        ) {
          const updatedConversation = {
            ...selectedConversation,
            ...message,
          };
          instagramConversations$.next({
            type: "update",
            conversation: updatedConversation as InstagramConversationModel,
          });
          selectConversation(
            updatedConversation as InstagramConversationModel,
            true
          );
        }
      }

      // Find existing conversation and update it
      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.inserted_at <= message.inserted_at &&
        message.msg_status === "new"
      ) {
        const updatedConversation: InstagramConversationModel = {
          ...existingConversation,
          ...message,
        } as InstagramConversationModel;

        if (message.type === "incoming") {
          updatedConversation.since = message.inserted_at;
        }
        // Prepend conversation to bump it to the top
        instagramConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        // Also update selected conversation if needed
        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingInstagramMessages$,
    conversations,
    selectedConversation,
    selectConversation,
    resetConversations,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  // Empty state content
  const emptyStateContent = useMemo(() => {
    if (conversations.length === 0 && !searchQuery) {
      if (selectedFilter === CRMConversationFilter.active) {
        return (
          <div className="mx-auto mt-40 flex w-full max-w-[340px] flex-col items-center justify-center gap-2 p-5 text-center">
            {t({
              id: "client-support-page.you-will-get-notification-once-receive-message",
              defaultMessage:
                "You will get a notification once you receive message from Clients 🙌",
            })}
          </div>
        );
      }

      return (
        <div className="mt-6 flex w-full justify-center px-5 text-center">
          <span className="font-secondary text-[15px] leading-4 text-[#828282]">
            {t({
              id: "client-support-page.no-expired-tickets",
              defaultMessage: "No Expired Tickets 🤩",
            })}
          </span>
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <span className="font-secondary mt-10 text-center text-sm leading-[160%] text-[#828282]">
          {t({
            id: "client-support-page.no-results",
            defaultMessage: "No results 😔",
          })}
        </span>
      );
    }

    return null;
  }, [conversations.length, searchQuery, selectedFilter, t]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <InstagramConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
          isDraftUnavailable={selectedFilter === CRMConversationFilter.expired}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      {emptyStateContent}
    </DataLoadingWrapper>
  );
};
